import { Producto, Supplier, Alerta, Recipe, MovimientoInventario } from '../types';

// Use Netlify Functions in production, local functions in development
const API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:4888/.netlify/functions'
  : '/.netlify/functions';

class ApiService {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }
    return response.json();
  }

  // Obtener todo el inventario
  async getAllInventory(): Promise<Producto[]> {
    try {
      const response = await fetch(`${API_URL}/inventory-get-all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Producto[]>(response);
    } catch (error) {
      console.error('Error obteniendo inventario:', error);
      throw error;
    }
  }

  // Obtener un producto por ID
  async getProductById(productId: string): Promise<Producto | null> {
    try {
      const response = await fetch(`${API_URL}/inventory-get-one?id=${productId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Producto>(response);
    } catch (error) {
      console.error('Error obteniendo producto:', error);
      return null;
    }
  }

  // Get transactions from MongoDB
  async getTransactions(params?: { 
    startDate?: string; 
    endDate?: string; 
    limit?: number;
    needsProducts?: boolean;
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.needsProducts) queryParams.append('needsProducts', 'true');
      
      const url = `${API_URL}/transactions-get${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await this.handleResponse<any>(response);
      // API returns { success, data: { transactions, pagination, summary } }
      const inner = data.data || data;
      return inner.transactions || inner || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  // Trigger GitLab pipeline for date range products
  async triggerProductExtraction(startDate: string, endDate: string): Promise<any> {
    try {
      const response = await fetch(`${API_URL}/trigger-gitlab-pipeline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          endDate,
          gitlabToken: process.env.REACT_APP_GITLAB_TOKEN
        })
      });
      return this.handleResponse<any>(response);
    } catch (error) {
      console.error('Error triggering product extraction:', error);
      throw error;
    }
  }

  // Crear un nuevo producto
  async createProduct(producto: Producto): Promise<Producto> {
    try {
      const response = await fetch(`${API_URL}/inventory-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(producto),
      });
      const result = await this.handleResponse<{ success: boolean; product: Producto }>(response);
      return result.product;
    } catch (error) {
      console.error('Error creando producto:', error);
      throw error;
    }
  }

  // Actualizar un producto
  async updateProduct(productId: string, updates: Partial<Producto>): Promise<boolean> {
    try {
      console.log('API updateProduct - Sending update for:', productId);
      console.log('API updateProduct - Product name:', updates.producto);
      console.log('API updateProduct - Updates object:', JSON.stringify(updates, null, 2));
      
      const requestBody = { productId, updates };
      console.log('API updateProduct - Full request body:', JSON.stringify(requestBody, null, 2));
      
      const response = await fetch(`${API_URL}/inventory-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        console.error('API updateProduct - HTTP error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('API updateProduct - Error response:', errorText);
      }
      
      const result = await this.handleResponse<{ success: boolean }>(response);
      console.log('API updateProduct - Response for', productId, ':', result);
      return result.success;
    } catch (error) {
      console.error('Error actualizando producto:', productId, error);
      throw error;
    }
  }

  // Eliminar un producto
  async deleteProduct(productId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/inventory-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId }),
      });
      const result = await this.handleResponse<{ success: boolean }>(response);
      return result.success;
    } catch (error) {
      console.error('Error eliminando producto:', error);
      throw error;
    }
  }

  // Insertar múltiples productos (para migración)
  async bulkInsertInventory(productos: Producto[]): Promise<number> {
    try {
      const response = await fetch(`${API_URL}/inventory/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ products: productos }),
      });
      const result = await this.handleResponse<{ success: boolean; insertedCount: number; message: string }>(response);
      return result.insertedCount;
    } catch (error) {
      console.error('Error en inserción masiva:', error);
      throw error;
    }
  }

  // Obtener productos con stock bajo
  async getLowStockProducts(): Promise<Producto[]> {
    try {
      const response = await fetch(`${API_URL}/inventory/low-stock`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Producto[]>(response);
    } catch (error) {
      console.error('Error obteniendo productos con stock bajo:', error);
      throw error;
    }
  }

  // Obtener productos por categoría
  async getProductsByCategory(categoria: string): Promise<Producto[]> {
    try {
      const response = await fetch(`${API_URL}/inventory/category/${encodeURIComponent(categoria)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Producto[]>(response);
    } catch (error) {
      console.error('Error obteniendo productos por categoría:', error);
      throw error;
    }
  }

  // Obtener estadísticas del inventario
  async getInventoryStats(): Promise<{ totalProductos: number; stockTotalValue: number; productosStockBajo: number }> {
    try {
      const response = await fetch(`${API_URL}/inventory-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      throw error;
    }
  }

  // Verificar si el API está disponible
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/health`, {
        method: 'GET',
      });
      return response.ok;
    } catch (error) {
      console.error('API no disponible:', error);
      return false;
    }
  }

  // ========== SUPPLIER OPERATIONS ==========

  // Get all suppliers
  async getAllSuppliers(): Promise<Supplier[]> {
    try {
      const response = await fetch(`${API_URL}/suppliers-get-all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Supplier[]>(response);
    } catch (error) {
      console.error('Error obteniendo proveedores:', error);
      throw error;
    }
  }

  // Get active suppliers only
  async getActiveSuppliers(): Promise<Supplier[]> {
    try {
      const response = await fetch(`${API_URL}/suppliers-active`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Supplier[]>(response);
    } catch (error) {
      console.error('Error obteniendo proveedores activos:', error);
      throw error;
    }
  }

  // Create a new supplier
  async createSupplier(supplier: Omit<Supplier, '_id' | 'fechaCreacion' | 'ultimaActualizacion'>): Promise<Supplier> {
    try {
      const response = await fetch(`${API_URL}/suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(supplier),
      });
      const result = await this.handleResponse<{ success: boolean; supplier: Supplier }>(response);
      return result.supplier;
    } catch (error) {
      console.error('Error creando proveedor:', error);
      throw error;
    }
  }

  // Update a supplier
  async updateSupplier(supplierId: string, updates: Partial<Supplier>): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/suppliers/${supplierId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      const result = await this.handleResponse<{ success: boolean }>(response);
      return result.success;
    } catch (error) {
      console.error('Error actualizando proveedor:', error);
      throw error;
    }
  }

  // Delete (deactivate) a supplier
  async deleteSupplier(supplierId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const result = await this.handleResponse<{ success: boolean }>(response);
      return result.success;
    } catch (error) {
      console.error('Error eliminando proveedor:', error);
      throw error;
    }
  }

  // Migrate suppliers from inventory
  async migrateSuppliers(): Promise<{ success: boolean; message: string; suppliers: Supplier[] }> {
    try {
      const response = await fetch(`${API_URL}/suppliers/migrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error migrando proveedores:', error);
      throw error;
    }
  }

  // ========== ALERT OPERATIONS ==========

  // Get all active alerts
  async getAllAlerts(): Promise<Alerta[]> {
    try {
      const response = await fetch(`${API_URL}/alerts-get`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Alerta[]>(response);
    } catch (error) {
      console.error('Error obteniendo alertas:', error);
      throw error;
    }
  }

  // Get cleared/resolved alerts (history)
  async getClearedAlerts(): Promise<Alerta[]> {
    try {
      const response = await fetch(`${API_URL}/alerts/cleared`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse<Alerta[]>(response);
    } catch (error) {
      console.error('Error obteniendo alertas resueltas:', error);
      throw error;
    }
  }

  // Create a new alert
  async createAlert(alert: Omit<Alerta, '_id'>): Promise<Alerta> {
    try {
      const response = await fetch(`${API_URL}/alerts-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(alert),
      });
      const result = await this.handleResponse<{ success: boolean; alert: Alerta }>(response);
      return result.alert;
    } catch (error) {
      console.error('Error creando alerta:', error);
      throw error;
    }
  }

  // Clear/resolve an alert
  async clearAlert(alertId: string, userId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/alerts/${alertId}/clear`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });
      const result = await this.handleResponse<{ success: boolean }>(response);
      return result.success;
    } catch (error) {
      console.error('Error resolviendo alerta:', error);
      throw error;
    }
  }

  // Delete an alert permanently
  async deleteAlert(alertId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/alerts/${alertId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const result = await this.handleResponse<{ success: boolean }>(response);
      return result.success;
    } catch (error) {
      console.error('Error eliminando alerta:', error);
      throw error;
    }
  }

  // ========== RECIPE OPERATIONS ==========

  async getAllRecipes(): Promise<Recipe[]> {
    try {
      const response = await fetch(`${API_URL}/recipes-get-all`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await this.handleResponse<{ success: boolean; recipes: Recipe[] }>(response);
      return result.recipes;
    } catch (error) {
      console.error('Error obteniendo recetas:', error);
      throw error;
    }
  }

  async createRecipe(recipe: Omit<Recipe, '_id' | 'createdAt' | 'updatedAt'>): Promise<Recipe> {
    try {
      const response = await fetch(`${API_URL}/recipes-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recipe),
      });
      const result = await this.handleResponse<{ success: boolean; recipe: Recipe }>(response);
      return result.recipe;
    } catch (error) {
      console.error('Error creando receta:', error);
      throw error;
    }
  }

  async deleteRecipe(menuItemName: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/recipes-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ menuItemName }),
      });
      const result = await this.handleResponse<{ success: boolean }>(response);
      return result.success;
    } catch (error) {
      console.error('Error eliminando receta:', error);
      throw error;
    }
  }

  // ========== INVENTORY DEDUCTION ==========

  async deductInventory(products: Array<{ name: string; quantity: number }>, reference: string): Promise<any> {
    try {
      const response = await fetch(`${API_URL}/inventory-deduct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products, reference }),
      });
      return this.handleResponse<any>(response);
    } catch (error) {
      console.error('Error deduciendo inventario:', error);
      throw error;
    }
  }

  // ========== MOVIMIENTOS ==========

  async getMovimientos(params?: {
    startDate?: string;
    endDate?: string;
    tipo?: string;
    productoId?: string;
    limit?: number;
  }): Promise<MovimientoInventario[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);
      if (params?.tipo) queryParams.append('tipo', params.tipo);
      if (params?.productoId) queryParams.append('productoId', params.productoId);
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const url = `${API_URL}/movimientos-get${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await this.handleResponse<{ success: boolean; movimientos: MovimientoInventario[] }>(response);
      return result.movimientos;
    } catch (error) {
      console.error('Error obteniendo movimientos:', error);
      throw error;
    }
  }

  // Check and generate alerts for a product (called after inventory updates)
  async checkAndGenerateAlerts(productoId: string): Promise<Alerta[]> {
    try {
      const response = await fetch(`${API_URL}/alerts/check/${productoId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const result = await this.handleResponse<{ success: boolean; alerts: Alerta[] }>(response);
      return result.alerts;
    } catch (error) {
      console.error('Error verificando alertas:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();